"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useModal } from "@/contexts/ModalContext";
import { cdnCommonPath } from "@/constants/cdnUrls";

const modalVariants = cva(
  [
    "relative bg-white shadow-strong",
    "w-full overflow-hidden flex flex-col",
    "focus:outline-none",
  ],
  {
    variants: {
      size: {
        sm: "md:max-w-sm max-w-full",
        md: "md:max-w-md max-w-full",
        lg: "md:max-w-lg max-w-full",
        xl: "md:max-w-xl max-w-full",
        "2xl": "md:max-w-2xl max-w-full",
        "3xl": "md:max-w-3xl max-w-full",
        "4xl": "md:max-w-4xl max-w-full",
        "5xl": "md:max-w-5xl max-w-full",
        full: "md:max-w-[95vw] max-w-full",
        auto: "md:max-w-fit max-w-full",
      },
      variant: {
        default: "",
        success: "",
        error: "",
        warning: "",
        info: "",
      },
      isBottomSheet: {
        true: "md:rounded-2xl md:max-h-[90dvh]", // Sadece desktop'ta rounded
        false: "rounded-2xl max-h-[90dvh]",
      },
      isFullScreen: {
        true: "h-screen max-h-screen md:max-h-[90dvh]",
        false: "max-h-[90dvh]",
      },
      animationState: {
        entering: "transform transition-all duration-300 ease-out",
        exiting: "transform transition-all duration-300 ease-in",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
      isBottomSheet: false,
      isFullScreen: false,
      animationState: "entering",
    },
    compoundVariants: [
      // isBottomSheet + isFullScreen: mobilde rounded yok, desktop'ta rounded var
      {
        isBottomSheet: true,
        isFullScreen: true,
        class: "h-screen max-h-screen md:max-h-[90dvh]",
      },
      // isBottomSheet + NOT fullScreen: mobilde rounded-t-2xl, desktop'ta rounded-2xl
      {
        isBottomSheet: true,
        isFullScreen: false,
        class: "rounded-t-2xl md:rounded-2xl max-h-[90dvh]",
      },
    ],
  },
);

// Variant'lara göre icon ve renk mapping'i
const getVariantConfig = (variant) => {
  const configs = {
    success: {
      icon: `${cdnCommonPath}/success.svg`,
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      titleColor: "text-green-900",
      buttonColor: "bg-green-600 hover:bg-green-700",
    },
    error: {
      icon: `${cdnCommonPath}/error.svg`,
      iconColor: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      titleColor: "text-red-900",
      buttonColor: "bg-red-600 hover:bg-red-700",
    },
    warning: {
      icon: `${cdnCommonPath}/warning.svg`,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      titleColor: "text-yellow-900",
      buttonColor: "bg-yellow-600 hover:bg-yellow-700",
    },
    info: {
      icon: `${cdnCommonPath}/info.svg`,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      titleColor: "text-blue-900",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
    },
    default: {
      icon: `${cdnCommonPath}/info.svg`,
      iconColor: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      titleColor: "text-gray-900",
      buttonColor: "bg-gray-600 hover:bg-gray-700",
    },
  };

  return configs[variant] || configs.default;
};

// Internal Modal bileşeni - animasyonlu geçişlerle
const InternalModal = ({
  isOpen,
  onClose,
  onCancel, // Yeni prop - helper modal'lar için
  title,
  children,
  size = "md",
  variant = "default",
  isBottomSheet = false, // Boolean olarak değişti
  isFullScreen = false, // Yeni prop - sadece bottomSheet'i etkiler
  useSafeViewport = false, // Mobil tarayıcı toolbar (vh) sorunları için opt-in
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  showBackdrop = true,
  modalContainerClassName,
  backdropClassName, // Yeni prop - backdrop rengi için
  contentClassName, // Yeni prop - content div class'ları için
  footerClassName, // Yeni prop - footer div class'ları için
  headerClassName, // Yeni prop - header div class'ları için
  _titleCentered = true, // Yeni prop - title'ı ortalamak için
  loading = false,
  className,
  footer,
  titleWrapperClassName,
  iconWrapperClassName,
  titleClassName, // Yeni prop - h2 elementine class eklemek için
  hideTitleBorder = false,
  id, // id prop'u - data-modal-id attribute için
  /** Sadece `isBottomSheet` iken modal paneline eklenir; `className` ile birlikte kullanılabilir */
  bottomSheetClassName = null,
  ..._props
}) => {
  const modalRef = useRef(null);
  const backdropRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Senkron state güncellemeleri render sırasında (React-approved pattern)
  if (isOpen && !shouldRender) {
    setShouldRender(true);
  }
  if (!isOpen && isAnimating) {
    setIsAnimating(false);
  }

  // Use existing hooks
  const escapePressed = useKeyPressed("Escape");
  useLockBodyScroll(isOpen);

  // Animasyon geçişleri ve DOM side-effect'leri
  useEffect(() => {
    if (isOpen) {
      document.body.dataset.modalOpen = "1";
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      delete document.body.dataset.modalOpen;
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key - loading sırasında escape'i engelle
  useEffect(() => {
    if (escapePressed && closeOnEscape && isOpen && !loading && onClose) {
      onClose();
    }
  }, [escapePressed, closeOnEscape, isOpen, onClose, loading]);

  // Handle backdrop click (memoized) - loading sırasında backdrop click'i engelle
  const handleBackdropClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (
        closeOnBackdropClick &&
        e.target === backdropRef.current &&
        !loading &&
        onClose
      ) {
        onClose();
      }
    },
    [closeOnBackdropClick, loading, onClose],
  );

  // Focus management
  useEffect(() => {
    if (isAnimating && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isAnimating]);

  // Animasyon sınıfları
  const backdropClasses = useMemo(() => {
    // Container classes burada kullanılıyor
    const baseContainerClasses = isBottomSheet
      ? "fixed inset-0 z-50 flex items-end justify-center md:items-center md:justify-center p-0 md:p-4"
      : "fixed inset-0 z-50 flex items-center justify-center p-4";

    // Backdrop için de farklı easing
    const backdropTransition = isAnimating
      ? "transition-all duration-300 ease-out"
      : "transition-all duration-300 ease-in";

    return clsx(
      baseContainerClasses,
      backdropTransition,
      showBackdrop && isAnimating
        ? backdropClassName || "bg-black/50"
        : "bg-transparent",
    );
  }, [isBottomSheet, showBackdrop, isAnimating, backdropClassName]);

  // DÜZELTME: Bottom sheet için responsive animasyon sınıfları
  const modalClasses = useMemo(() => {
    // Animasyon state'ini belirle
    const animationState = isAnimating ? "entering" : "exiting";
    const baseClasses = modalVariants({
      size,
      variant,
      isBottomSheet,
      isFullScreen: isBottomSheet && isFullScreen, // isFullScreen sadece bottomSheet için geçerli
      animationState,
    });

    const safeViewportClasses =
      useSafeViewport && isBottomSheet
        ? clsx(
            // Modern viewport units: svh (stable) -> dvh (dynamic) fallback
            isFullScreen
              ? "supports-[height:100dvh]:h-[100dvh] supports-[height:100dvh]:max-h-[100dvh] supports-[height:100svh]:h-[100svh] supports-[height:100svh]:max-h-[100svh]"
              : "supports-[height:100dvh]:max-h-[90dvh] supports-[height:100svh]:max-h-[90svh]",
            // Desktop tarafında da (md) 90vh yerine 90svh/dvh kullan
            isFullScreen
              ? "md:supports-[height:100dvh]:max-h-[90dvh] md:supports-[height:100svh]:max-h-[90svh]"
              : "md:supports-[height:100dvh]:max-h-[90dvh] md:supports-[height:100svh]:max-h-[90svh]",
          )
        : "";

    if (isAnimating) {
      // Modal açık - görünür durumda
      return clsx(
        baseClasses,
        safeViewportClasses,
        "translate-y-0 scale-100 opacity-100",
        className,
        isBottomSheet && bottomSheetClassName,
      );
    }

    // Modal kapalı - gizli durumda
    if (isBottomSheet) {
      // Bottom sheet: responsive animasyon
      return clsx(
        baseClasses,
        safeViewportClasses,
        // Mobil: sadece kayma animasyonu, opacity sabit
        "translate-y-full scale-100 opacity-100",
        // Desktop: normal modal animasyonu (opacity + scale + translate)
        "md:translate-y-4 md:scale-95 md:opacity-0",
        className,
        bottomSheetClassName,
      );
    }

    // Normal modal: her zaman aynı animasyon
    return clsx(baseClasses, "translate-y-4 scale-95 opacity-0", className);
  }, [
    size,
    variant,
    isAnimating,
    isBottomSheet,
    isFullScreen,
    useSafeViewport,
    className,
    bottomSheetClassName,
  ]);

  // shouldRender false ise hiçbir şey render etme
  if (!shouldRender) return null;

  const modalContent = (
    <div
      ref={backdropRef}
      className={clsx(backdropClasses, modalContainerClassName)}
      onClick={handleBackdropClick}
      data-modal-id={id}
    >
      <div
        ref={modalRef}
        className={modalClasses}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        onClick={(e) => {
          // Modal içeriğine tıklandığında event propagation'ı durdur
          // Böylece kartın onClick handler'ına gitmez
          e.stopPropagation();
        }}
      >
        {/* Header - Sabit */}
        {(title || showCloseButton) && (
          <div
            className={clsx(
              ["success", "error", "warning", "info"].includes(variant)
                ? "bor items-start px-6 pt-5 pb-0"
                : "items-center px-4 py-5",
              "flex shrink-0 justify-between",
              headerClassName,
              !hideTitleBorder &&
                !["success", "error", "warning", "info"].includes(variant) &&
                "border-border-weak border-b",
            )}
          >
            <span
              className={clsx(
                "flex items-center justify-center",
                iconWrapperClassName,
                !["success", "error", "warning", "info"].includes(variant) &&
                  !iconWrapperClassName &&
                  "w-6",
              )}
            >
              {/* Icon sadece themed modal'larda başlık yanında */}
              {["success", "error", "warning", "info"].includes(variant) && (
                <Image
                  src={getVariantConfig(variant).icon}
                  width={54}
                  height={54}
                  alt={variant}
                  priority
                />
              )}
            </span>
            {title &&
              !["success", "error", "warning", "info"].includes(variant) && (
                <div
                  className={clsx(
                    "space-x-3 text-center",
                    titleWrapperClassName,
                  )}
                >
                  <h2
                    id="modal-title"
                    className={clsx(
                      "text-foreground text-lg font-semibold",
                      titleClassName,
                    )}
                  >
                    {title}
                  </h2>
                </div>
              )}
            {showCloseButton && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Event propagation'ı durdur - mobilde paket seçimi modalının açılmasını engelle
                  // Loading sırasında çarpı butonunu engelle
                  if (loading) return;

                  // Eğer onCancel varsa (helper modal), cancel çağır
                  // Yoksa normal close
                  if (onCancel) {
                    onCancel();
                  } else if (onClose) {
                    onClose();
                  }
                }}
                disabled={loading}
                className={clsx(
                  "leading-none transition-colors",
                  loading
                    ? "cursor-not-allowed text-gray-300"
                    : "text-primary-muted-subtext cursor-pointer hover:text-gray-400",
                )}
                aria-label="Close modal"
              >
                <FontAwesomeIcon
                  icon={byPrefixAndName.fak["x-mark-custom"]}
                  className={clsx(
                    ["success", "error", "warning", "info"].includes(variant)
                      ? "text-foreground-muted text-lg"
                      : "text-foreground text-base-line h-6! w-6!",
                  )}
                />
              </button>
            )}
          </div>
        )}

        {/* Content - Scroll edilebilir */}
        <div
          className={
            contentClassName ||
            "scrollbar-hide flex-1 overflow-y-auto p-6 md:px-6 md:pt-4 md:pb-5"
          }
        >
          {["success", "error", "warning", "info"].includes(variant) &&
            title && (
              <div className="mb-2 text-left text-lg font-semibold">
                {title}
              </div>
            )}

          {children}
        </div>

        {/* Footer - Sabit */}
        {footer && (
          <div
            className={
              footerClassName ||
              "border-border bg-background-100 flex shrink-0 items-center justify-end gap-3 border-t p-6"
            }
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Portal to body
  return createPortal(modalContent, document.body);
};

// Geliştirilmiş DefaultModalContent - Normal versiyon
const DefaultModalContent = ({ modal, config }) => {
  const {
    message,
    showCancel = true,
    variant = "default",
    isAsyncConfirm = false,
    contentWrapperClassName,
    messageWrapperClassName,
    messageClassName,
    actionButtonsWrapperClassName: _actionButtonsWrapperClassName,
    cancelButtonClassName,
    applyButtonClassName,
    cancelButtonText,
    confirmButtonText,
  } = config;

  const t = useTranslationDetector();

  // Loading state'i modal'dan al
  const loading = modal.loading || false;

  if (!message) return null;

  return (
    <div className={clsx("space-y-6", contentWrapperClassName)}>
      {/* Message */}
      <div className={clsx("pt-1", messageWrapperClassName)}>
        <div
          className={clsx("leading-relaxed text-gray-700", messageClassName)}
        >
          {message}
        </div>
      </div>

      {/* Action Buttons */}
      <div
        className={clsx("flex items-center gap-3", {
          // default & showCancel yok → sağa yasla
          "justify-end":
            !["success", "error", "warning", "info"].includes(variant) &&
            variant !== "confirm",

          // confirm → yan yana eşit dağıt
          "justify-between": variant === "confirm",
        })}
      >
        {showCancel && (
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              if (!loading) {
                if (config.reject) {
                  config.reject();
                } else {
                  modal.cancel();
                }
              }
            }}
            disabled={loading}
            className={clsx(
              {
                // confirm → eşit genişlik
                "flex-1":
                  ["success", "error", "warning", "info"].includes(variant) &&
                  showCancel,

                // diğer tüm durumlar
                "w-auto":
                  !["success", "error", "warning", "info"].includes(variant) &&
                  variant !== "confirm",
                cancelButtonClassName,
              },
              cancelButtonClassName,
            )}
          >
            {cancelButtonText || t("common.cancel")}
          </Button>
        )}

        <Button
          onClick={() => {
            if (!loading) {
              // Eğer async confirm ise, modal'ı kapatmadan resolve et
              if (isAsyncConfirm && config.resolveWithoutClosing) {
                config.resolveWithoutClosing();
              } else {
                modal.confirm();
              }
            }
          }}
          disabled={loading}
          loading={loading}
          size="lg"
          className={clsx(
            {
              // success / error / warning / info → tek buton full
              "w-full": ["success", "error", "warning", "info"].includes(
                variant,
              ),

              // confirm → eşit genişlik
              "flex-1":
                ["success", "error", "warning", "info"].includes(variant) &&
                showCancel,
              applyButtonClassName,
            },
            applyButtonClassName,
          )}
        >
          {confirmButtonText || t("common.okay")}
        </Button>
      </div>
    </div>
  );
};

// Ana Modal bileşeni - context kullanan tek public API
const Modal = ({ id, children, ...props }) => {
  const modal = useModal(id);

  // children'ı hem prop'tan hem de config'ten al
  const childrenFromConfig = modal.config?.children;
  const childrenToUse = children || childrenFromConfig;

  // Config'teki onClose varsa hem onu hem de modal.close'u çağır
  // Geriye uyumluluk için: onClose yoksa sadece modal.close çağrılır
  const handleClose = useCallback(() => {
    if (modal.config?.onClose) {
      modal.config.onClose();
    }
    modal.close();
  }, [modal]);

  // Cancel için de onClose callback'ini çağır
  const handleCancel = useCallback(() => {
    if (modal.config?.onClose) {
      modal.config.onClose();
    }
    modal.cancel();
  }, [modal]);

  // Confirm için de onClose callback'ini çağır
  const handleConfirm = useCallback(() => {
    if (modal.config?.onClose) {
      modal.config.onClose();
    }
    modal.confirm();
  }, [modal]);

  // Modal objesini extend et - wrapper fonksiyonları ekle
  const extendedModal = useMemo(
    () => ({
      ...modal,
      cancel: handleCancel,
      confirm: handleConfirm,
      close: handleClose,
    }),
    [modal, handleCancel, handleConfirm, handleClose],
  );

  // Helper metodları için default content
  const shouldUseDefaultContent = modal.config?.message && !childrenToUse;

  // Eğer children bir fonksiyon ise, modal ve config'i prop olarak geçir
  const modalChildren = shouldUseDefaultContent ? (
    <DefaultModalContent modal={extendedModal} config={modal.config} />
  ) : typeof childrenToUse === "function" ? (
    childrenToUse({ modal: extendedModal, config: modal.config })
  ) : (
    childrenToUse
  );

  // Config'ten onClose'u çıkar çünkü handleClose kullanıyoruz
  // Diğer prop'ları spread ederken onClose'u exclude et
  const { onClose: _, ...restConfig } = modal.config || {};

  return (
    <InternalModal
      isOpen={modal.isOpen}
      onClose={handleClose}
      onCancel={modal.config?.message ? handleCancel : undefined} // Helper modal'larda cancel fonksiyonu
      loading={modal.loading} // Loading state'i geç
      id={id} // id prop'unu InternalModal'a geç
      {...restConfig}
      {...props}
    >
      {modalChildren}
    </InternalModal>
  );
};

export { InternalModal, Modal };
export default Modal;
